import { useProducts } from "./use-products";
import { Button } from "@ui/buttons/button";
import { Trans } from "@ui/i18n/trans";
import { ForumIcon } from "@ui/icons/material/Forum";
import { Navbar } from "../../ui/navigation/navbar/navbar";
import { Navigate, useParams } from "react-router-dom";
import { Footer } from "../../ui/footer/footer";
import { Fragment, useEffect, useState } from "react";
import {
  findBestMonthlyPrice,
  findBestYearlyPrice,
  UpsellBillingCycle,
} from "./find-best-price";
import { BillingCycleRadio } from "./billing-cycle-radio";
import { StaticPageTitle } from "../../seo/static-page-title";
import { PricingTable } from "@common/billing/pricing-table/pricing-table";
import { useIsMobileMediaQuery } from "@ui/utils/hooks/is-mobile-media-query";
import { getBootstrapData } from "@ui/bootstrap-data/bootstrap-data-store";
import clsx from "clsx";
import { Product } from "../product";
import { useAuth } from "@common/auth/use-auth";
import { isMobileApp } from "@common/core/platform-detection";
import { getLastPlayerAmbientStyle } from "@app/web-player/layout/use-player-ambient-style";

const PRICING_ROLE_ALIASES: Record<string, string> = {
  user: "user",
  users: "user",
  artist: "artist",
  artists: "artist",
  creator: "creator",
  creators: "creator",
  author: "author",
  authors: "author",
  label: "label",
  labels: "label",
  distributor: "distributor",
  distributors: "distributor",
};

const PRICING_ROLE_SLUG_PRIORITY = [
  "artist",
  "creator",
  "label",
  "distributor",
  "author",
  "user",
];

export function PricingPage() {
  const { role: roleParam } = useParams<{ role: string }>();
  const { user } = useAuth();
  const isNativeApp = isMobileApp();

  const normalizedRole = normalizePricingRoleSlug(roleParam);
  const targetRole =
    normalizedRole ||
    getPricingRoleForCurrentUser(user?.roles?.map((role) => role.name));

  const query = useProducts("pricingPage", targetRole);
  const [selectedCycle, setSelectedCycle] =
    useState<UpsellBillingCycle>("yearly");
  const isMobile = useIsMobileMediaQuery();
  const ambientStyle = getLastPlayerAmbientStyle();

  useEffect(() => {
    if (query.data?.products) {
      setSelectedCycle(getDefaultCycle(query.data.products));
    }
  }, [query.data?.products]);

  useEffect(() => {
    if (!isNativeApp) {
      return;
    }

    document.body.classList.add("player-ambient-app-active");

    return () => {
      document.body.classList.remove("player-ambient-app-active");
    };
  }, [isNativeApp]);

  useEffect(() => {
    if (!isNativeApp) {
      return;
    }

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [isNativeApp]);

  if (!roleParam) {
    return <Navigate to={`/pricing/${targetRole}`} replace />;
  }

  if (normalizedRole !== roleParam.toLowerCase()) {
    return <Navigate to={`/pricing/${normalizedRole}`} replace />;
  }

  return (
    <Fragment>
      <StaticPageTitle>
        <Trans message="Pricing" />
      </StaticPageTitle>
      {isNativeApp && (
        <div
          className="app-ambient-backdrop player-ambient-scene"
          style={ambientStyle}
        />
      )}
      <div className={clsx(isNativeApp && "app-ambient-content")}>
        <Navbar
          menuPosition="pricing-table-page"
          showBackButton={true}
          wrapInContainer={true}
          size="md"
          hideOnScroll={isNativeApp}
          color={isNativeApp ? "transparent" : undefined}
          darkModeColor={isNativeApp ? "transparent" : undefined}
          className={clsx(isNativeApp && "app-ambient-header")}
        />
        <main
          className={clsx(
            isNativeApp
              ? "app-ambient-main relative h-screen overflow-y-auto overflow-x-hidden pb-124 stable-scrollbar"
              : undefined,
          )}
        >
          <div
            className={clsx(
              "container mx-auto px-24",
              isMobile &&
                (isNativeApp ? "app-native-dashboard-content-offset" : "pt-54"),
            )}
          >
            <h1 className="mb-30 mt-30 text-center text-3xl font-normal md:mt-60 md:text-4xl md:font-medium">
              <Trans
                message="Choose the right:brplan for you"
                values={{ br: <br /> }}
              />
            </h1>

            {query.data?.products &&
            getAvailableBillingCycles(query.data.products).length > 1 ? (
              <BillingCycleRadio
                products={query.data.products}
                selectedCycle={selectedCycle}
                onChange={setSelectedCycle}
                className="mb-40 flex justify-center md:mb-70"
                size="lg"
              />
            ) : (
              <div className="mb-40 h-30 md:mb-70" />
            )}

            <PricingTable
              selectedCycle={selectedCycle}
              productLoader="pricingPage"
              role={targetRole}
            />
            <ContactSection />
          </div>
          <Footer className="container mx-auto flex-shrink-0 px-24" />
        </main>
      </div>
    </Fragment>
  );
}

function getDefaultCycle(products: Product[]): UpsellBillingCycle {
  let monthlyCount = 0;
  let yearlyCount = 0;

  for (const product of products) {
    if (findBestMonthlyPrice(product.prices)) monthlyCount++;
    if (findBestYearlyPrice(product.prices)) yearlyCount++;
  }

  return yearlyCount >= monthlyCount ? "yearly" : "monthly";
}

function getAvailableBillingCycles(products: Product[]): UpsellBillingCycle[] {
  const hasMonthly = products.some((p) => findBestMonthlyPrice(p.prices));
  const hasYearly = products.some((p) => findBestYearlyPrice(p.prices));

  const result: UpsellBillingCycle[] = [];
  if (hasMonthly) result.push("monthly");
  if (hasYearly) result.push("yearly");
  return result;
}

function ContactSection() {
  const supportUrl = getBootstrapData().settings.support_url;

  return (
    <div className="my-20 p-24 text-center md:my-80">
      {/*
      <ForumIcon size="xl" className="text-muted" />
      <div className="my-8 md:text-lg">
        <Trans message="Do you have any questions about PRO accounts?" />
      </div>
      <div className="mb-24 mt-20 text-sm md:mt-0 md:text-base">
        <Trans message="Our support team will be happy to assist you." />
      </div>
      <Button variant="flat" color="primary" elementType={Link} to="/contact">
        <Trans message="Contact us" />
      </Button>
      */}
      <ForumIcon size="xl" className="text-muted" />
      <div className="my-8 md:text-lg">
        <Trans message="Need help with your PRO account or have questions?" />
      </div>
      <div className="mb-24 mt-20 text-sm md:mt-0 md:text-base">
        <Trans message="Visit our support site to access the FAQ or open a support ticket." />
      </div>
      <Button
        variant="flat"
        color="primary"
        elementType="a"
        href={supportUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Trans message="Go to support site" />
      </Button>
    </div>
  );
}

function getPricingRoleForCurrentUser(userRoleNames?: string[]): string {
  if (!userRoleNames?.length) {
    return "user";
  }

  const normalizedUserRoleSlugs = userRoleNames
    .map((roleName) => normalizePricingRoleSlug(roleName))
    .filter((roleName): roleName is string => Boolean(roleName));

  for (const roleSlug of PRICING_ROLE_SLUG_PRIORITY) {
    if (normalizedUserRoleSlugs.includes(roleSlug)) {
      return roleSlug;
    }
  }

  return normalizedUserRoleSlugs[0] || "user";
}

function normalizePricingRoleSlug(role?: string | null): string | null {
  if (!role) return null;

  const normalized = role.toLowerCase().trim();
  const aliased = PRICING_ROLE_ALIASES[normalized];
  if (aliased) {
    return aliased;
  }

  const slug = normalized.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  return slug || "user";
}
